import { m as motion } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { getEmployees } from '@/shared/api/employees';
import { getHotels } from '@/shared/api/hotels';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AdditionalInfoSection from '@/shared/components/sections/AdditionalInfoSection';
import { AdditionalInfo } from '@/shared/components/sections/AdditionalInfoSection';
import HotelsSection, { HotelsSectionProps } from '@/shared/components/sections/HotelsSection';
import ServiceHeaderSection from '@/shared/components/sections/ServiceHeaderSection';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const ServicesPage = ({
    hotelsSectionProps,
    h1,
    pageTitle,
    titleDescription,
    bodyClass,
    breadcrumbs,
    meta,
    navigation,
    additionalInfoProps,
    buttonText,
    buttonLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);

    return (
        <DefaultLayout currentPageId={PAGES.HOTELS} bodyClass={bodyClass} ref={ref}>
            <h1 className="visually-hidden">{h1}</h1>
            <ServiceHeaderSection
                serviceHeaderSectionProps={{
                    breadcrumbs,
                    h1: pageTitle,
                    titleDescription,
                    navigation,
                    buttonText,
                    buttonLink,
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="services-wrapper"
            >
                <HotelsSection hotelsSectionProps={hotelsSectionProps} />
                <AdditionalInfoSection variant="large" additionalInfoSectionProps={additionalInfoProps} />
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-hotels"
                data={generateWebPageJsonLd({
                    name: pageTitle || h1,
                    description: titleDescription || meta.description,
                    pagePath: '/services/hotels/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ServicesPage;

type ServicesPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        pageTitle: string;
        navigation: AppNavigation;
        titleDescription: string;
        buttonText: string;
        buttonLink: string;
        hotelsSectionProps: HotelsSectionProps;
        additionalInfoProps: AdditionalInfo;
    }
>;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'hotels' } });

    const [commonPageProps, hotelsResponse, siteSettings, employeesResponse] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.HOTELS),
        getHotels(),
        getSiteSettings({
            params: {
                keys: [
                    'hotel_first_screen_title',
                    'hotel_first_screen_desription',
                    'hotel_first_screen_link',
                    'hotel_first_screen_link_text',

                    'hotel_hotels_title',
                    'hotel_hotels_desription',
                    'hotel_hotels_link',
                    'hotel_hotels_link_text',

                    'hotel_contact_title',
                    'hotel_contact_desription',
                ],
            },
        }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.HOTELS } }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'services-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Сервисы', href: '/services/' },
            ],
            h1: pageResponse.data?.meta?.title || 'Сервисы',
            pageTitle: siteSettings.data?.hotel_first_screen_title || 'Сервисы',
            titleDescription:
                siteSettings.data?.hotel_first_screen_desription ||
                'Используйте возможности сервисов, чтобы сделать ваше участие в событиях TNF максимально продуктивным',
            buttonText: siteSettings.data?.hotel_first_screen_link_text || 'Служба поддержки',
            buttonLink: siteSettings.data?.hotel_first_screen_link || '',
            navigation: [
                {
                    id: 0,
                    title: 'Бронирование гостиниц',
                    href: '/services/hotels/',
                },
                {
                    id: 1,
                    title: 'Аренда транспорта',
                    href: '/services/car-rent/',
                },
                {
                    id: 2,
                    title: 'Аккредитация транспорта',
                    href: '/services/car-accreditation/',
                },
                {
                    id: 3,
                    title: 'Шаттлы',
                    href: '/services/shuttles/',
                },
                {
                    id: 4,
                    title: 'Зоны питания',
                    href: '/services/food-areas/',
                },
            ],
            hotelsSectionProps: {
                title: siteSettings.data?.hotel_hotels_title || 'Гостиницы',
                subtitle:
                    siteSettings.data?.hotel_hotels_desription ||
                    'Ценовая политика, минимальный период бронирования и условия отмены бронирования устанавливаются гостиничными объектами самостоятельно.',
                linkText: siteSettings.data?.hotel_hotels_link_text || 'Отправить запрос',
                linkURL: siteSettings.data?.hotel_hotels_link || '',
                hotels: hotelsResponse.data || [],
            },
            additionalInfoProps: {
                title: siteSettings.data?.hotel_contact_title || 'Дополнительная информация',
                subtitle:
                    siteSettings.data?.hotel_contact_desription ||
                    'Получить дополнительную информацию об условиях бронирования можно у оператора Форума в сфере гостеприимства - компании Promo Travel:',
                contacts: employeesResponse.data || [],
            },
        } satisfies ServicesPageProps,
        revalidate: 60,
    };
};
